
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const EditPackagesPage = () => {
  // const imagePath=
  const { packagesId } = useParams();
  const navigate = useNavigate();
  const IMAGE_BASE_URL =
    process.env.REACT_APP_BASE_URL ||
    "";

  // =========================
  // STATES
  // =========================
  const [getPackagesName, setPackagesName] = useState("");
  const [getPackagesSlag, setPackagesSlag] = useState("");
  const [getPackagesMeta_title, setPackagesMeta_title] = useState("");
  const [getPackagesMeta_desc, setPackagesMeta_desc] = useState("");
  const [getPackagesMeta_keyword, setPackagesMeta_keyword] = useState("");
  const [getPackagesPage_desc, setPackagesPage_desc] = useState("");
  const [loading, setLoading] = useState(false);


  // =========================
  // MULTIPLE ROWS
  // =========================
  const [multipleRows, setMultipleRows] = useState([
    {
      miniTitle: "",
      miniDescription: "",
      miniImage: null,
      existingImage: "",
    },
  ]);

const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://") ||
      image.startsWith("blob:")
    ) {
      return image;
    }

    return `${IMAGE_BASE_URL}/uploads/holidays_packages/${image}`;
  };

  // =========================
  // SLUG GENERATOR
  // =========================
  const generateSlug = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  // =========================
  // FETCH SINGLE PACKAGE
  // =========================
  const fetchPackagesById = async () => {
    try {
      const res = await api.get(
        `/adminHolidays/get-packages-name-by-id/${packagesId}`
      );

      if (res.data.success) {
        const data = res.data.data;

        setPackagesName(data.packages_name || "");
        setPackagesSlag(data.packages_slug || "");
        setPackagesMeta_title(data.meta_title || "");
        setPackagesMeta_desc(data.meta_desc || "");
        setPackagesMeta_keyword(data.meta_keyword || "");
        setPackagesPage_desc(data.page_desc || "");

        // If the API returns mini rows, load them.
        // Get multiple rows
      let rows = data.multipleRows ?? data.miniRows ?? [];

      // Convert JSON string into an array
      try {
        while (typeof rows === "string") {
          rows = JSON.parse(rows);
        }
      } catch (parseError) {
        console.error("Multiple rows JSON error:", parseError);
        rows = [];
      }

      // Ensure rows is always an array
      if (!Array.isArray(rows)) {
        rows = [];
      }

      // Set default row if API returns no rows
      if (rows.length === 0) {
        rows = [
          {
            miniTitle: "",
            miniDescription: "",
            miniImage: null,
            existingImage: "",
          },
        ];
      }

      setMultipleRows(rows);
      } else {
        toast.error("Packages Name not found");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to fetch Packages Name"
      );
    }
  };

  // =========================
  // FETCH DATA
  // =========================
  useEffect(() => {
    if (packagesId) {
      fetchPackagesById();
    }
  }, [packagesId]);

  // =========================
  // UPDATE PACKAGE
  // =========================
  const updateCategory = async () => {
    if (!getPackagesName.trim()) {
      toast.error("Packages Name required");
      return;
    }

    const confirm = await Swal.fire({
      title: "Update Packages Name?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes Update",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      // Keep image files separate from the JSON row data.
      const rowsForApi = multipleRows.map((row, index) => ({
        miniTitle: row.miniTitle,
        miniDescription: row.miniDescription,
        existingImage: row.existingImage || "",
        imageIndex: row.miniImage ? index : null,
      }));

      const formData = new FormData();

      formData.append("packagesId", packagesId);
      formData.append("packages_name", getPackagesName.trim());
      formData.append("packages_slug", getPackagesSlag);
      formData.append("meta_title", getPackagesMeta_title);
      formData.append("meta_desc", getPackagesMeta_desc);
      formData.append("meta_keyword", getPackagesMeta_keyword);
      formData.append("page_desc", getPackagesPage_desc);
      formData.append("multipleRows", JSON.stringify(rowsForApi));

      // Append selected image files.
      multipleRows.forEach((row, index) => {
        if (row.miniImage instanceof File) {
          formData.append(
            "miniImages",
            row.miniImage,
            `${index}_${row.miniImage.name}`
          );
        }
      });

      const res = await api.put(
        "/adminHolidays/update-packages-name",
        formData
      );

      if (res.data.success) {
        await Swal.fire(
          "Success",
          "Packages Name Updated",
          "success"
        );

        navigate("/holidays/packages/manage-packages");
      } else {
        await Swal.fire(
          "Error",
          res.data.message || "Failed to update Packages Name",
          "error"
        );
      }
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.message || "Server Error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // ADD ROW
  // =========================
  const addMultipleRow = () => {
    setMultipleRows((prevRows) => [
      ...prevRows,
      {
        miniTitle: "",
        miniDescription: "",
        miniImage: null,
        existingImage: "",
      },
    ]);
  };

  // =========================
  // REMOVE ROW
  // =========================
  const removeMultipleRow = async (index) => {
    if (multipleRows.length === 1) {
      toast.error("At least one row is required");
      return;
    }

    const confirm = await Swal.fire({
      title: "Delete Row?",
      text: "Are you sure you want to delete this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    setMultipleRows((prevRows) =>
      prevRows.filter((_, i) => i !== index)
    );
  };

  // =========================
  // HANDLE ROW CHANGE
  // =========================
  const handleMultipleChange = (index, field, value) => {
    setMultipleRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  // =========================
  // CHARACTER COUNTS
  // =========================
  const metaTitleLength = getPackagesMeta_title.length;
  const metaDescLength = getPackagesMeta_desc.length;
  const metaKeywordLength = getPackagesMeta_keyword.length;

  // =========================
  // UI
  // =========================
  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      {/* HEADER */}
      <div className="card shadow-sm mb-4 bg-primary-subtle">
        <div className="card-body d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Edit Packages Name</h4>

          <Link
            to="/holidays/packages/manage-packages"
            className="btn btn-secondary btn-sm d-flex align-items-center gap-1"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
        </div>
      </div>

      {/* PACKAGE FORM */}
      <div className="card">
        <div className="card-body">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Packages Name</th>
                <th>Slug</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={getPackagesName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPackagesName(value);
                      setPackagesSlag(generateSlug(value));
                    }}
                    placeholder="Enter Packages Name"
                  />
                </td>

                <td>
                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    value={getPackagesSlag}
                  />
                </td>
              </tr>
            </tbody>

            <thead>
              <tr>
                <th>
                  Meta Title{" "}
                  <small
                    className={
                      metaTitleLength >= 80
                        ? "text-danger"
                        : "text-muted"
                    }
                  >
                    ({metaTitleLength}/80 Characters)
                  </small>
                </th>

                <th>
                  Meta Description{" "}
                  <small
                    className={
                      metaDescLength >= 160
                        ? "text-danger"
                        : "text-muted"
                    }
                  >
                    ({metaDescLength}/160 Characters)
                  </small>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <textarea
                    rows={3}
                    className="form-control"
                    maxLength={80}
                    value={getPackagesMeta_title}
                    onChange={(e) =>
                      setPackagesMeta_title(e.target.value)
                    }
                    placeholder="Enter Meta Title"
                  />
                </td>

                <td>
                  <textarea
                    rows={3}
                    className="form-control"
                    maxLength={160}
                    value={getPackagesMeta_desc}
                    onChange={(e) =>
                      setPackagesMeta_desc(e.target.value)
                    }
                    placeholder="Enter Meta Description"
                  />
                </td>
              </tr>
            </tbody>

            <thead>
              <tr>
                <th>
                  Meta Keyword{" "}
                  <small
                    className={
                      metaKeywordLength >= 255
                        ? "text-danger"
                        : "text-muted"
                    }
                  >
                    ({metaKeywordLength}/255 Characters)
                  </small>
                </th>
                <th>Page Description</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <textarea
                    rows={3}
                    maxLength={255}
                    className="form-control"
                    value={getPackagesMeta_keyword}
                    onChange={(e) =>
                      setPackagesMeta_keyword(e.target.value)
                    }
                    placeholder="Enter Meta Keywords"
                  />
                </td>

                <td>
                  <textarea
                    rows={3}
                    className="form-control"
                    value={getPackagesPage_desc}
                    onChange={(e) =>
                      setPackagesPage_desc(e.target.value)
                    }
                    placeholder="Enter Page Description"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {/* MULTIPLE ROWS */}
          <div className="table-responsive">
            <table className="table table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "60px" }}>Sl.No</th>
                  <th>Mini Title</th>
                  <th>Mini Description</th>
                  <th>Mini Image</th>
                  <th style={{ width: "120px" }}>Action</th>
                </tr>
              </thead>

              <tbody>
              
                
                {multipleRows.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Mini Title"
                        value={row.miniTitle}
                        onChange={(e) =>
                          handleMultipleChange(
                            index,
                            "miniTitle",
                            e.target.value
                          )
                        }
                      />
                    </td>

                    <td>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Enter Mini Description"
                        value={row.miniDescription}
                        onChange={(e) =>
                          handleMultipleChange(
                            index,
                            "miniDescription",
                            e.target.value
                          )
                        }
                      />
                    </td>

                    <td>
                      
{row.existingImage ? (
  // IF: Existing image available hai
  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
    <img
      src={getImageUrl(row.existingImage)}
      alt="Existing"
      style={{
        width: "60px",
        height: "60px",
        objectFit: "contain",
        border: "1px solid #ddd",
        borderRadius: "6px",
        padding: "4px",
        background: "#fff",
      }}
    />

    <input
      type="file"
      className="form-control"
      accept="image/*"
      onChange={(e) => {
        const file = e.target.files?.[0] || null;
        handleMultipleChange(index, "miniImage", file);
      }}
    />
  </div>
) : (
  // ELSE: Existing image nahi hai
  <input
    type="file"
    className="form-control"
    accept="image/*"
    onChange={(e) => {
      const file = e.target.files?.[0] || null;
      handleMultipleChange(index, "miniImage", file);
    }}
  />
)}

{row.miniImage && (
  <small className="text-success d-block mt-1">
    Selected: {row.miniImage.name}
  </small>
)}
                    </td>

                    <td>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={addMultipleRow}
                          title="Add Row"
                        >
                          <i className="fa fa-plus-square" />
                        </button>

                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeMultipleRow(index)}
                          title="Delete Row"
                        >
                          <i className="fa fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* UPDATE BUTTON */}
          <button
            type="button"
            className="btn btn-success mt-3"
            onClick={updateCategory}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Packages Name"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPackagesPage;
